import { DriveItem, FOLDER, FetchItemsFn } from '@dto/drive';
import { TW_AppSidebarMenuContainer, TW_AppSidebarMenuIcon, TW_AppSidebarMenuText, TW_DriveSidebar_Container, TW_DriveSidebar_FolderList, TW_DriveSidebar_FolderRow, TW_DriveSidebar_MembersFooter, TW_DriveSidebar_Title, TW_DriveSidebar_TitleBar, getWsButtonStyle, useHover } from '@utils/twClasses';
import { ArrowDown2, ArrowRight2, Folder2, People } from 'iconsax-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useModelStore } from 'src/store';

interface FolderNodeProps {
    folder: DriveItem;
    depth: number;
    selectedPath: string;
    onSelect: (path: string) => void;
    fetchItems: FetchItemsFn;
}

function FolderNode({ folder, depth, selectedPath, onSelect, fetchItems }: FolderNodeProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [children, setChildren] = useState<DriveItem[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [isHover, setIsHover] = useState(false);
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);

    const isSelected = selectedPath === folder.path;

    const toggle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!loaded) {
            const items = await fetchItems(folder.path);
            setChildren(items.filter(i => i.type === FOLDER));
            setLoaded(true);
        }
        setIsOpen(prev => !prev);
    };

    const handleSelect = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect(folder.path);
    };

    const style = isSelected
        ? getWsButtonStyle(wsLightColor, wsColor)
        : isHover
            ? getWsButtonStyle(wsHoverColor)
            : getWsButtonStyle('white');

    return (
        <div>
            <div
                className={TW_DriveSidebar_FolderRow}
                style={{ paddingLeft: `${(depth + 1) * 0.75}rem`, ...style }}
                onClick={handleSelect}
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
            >
                {isOpen
                    ? <ArrowDown2 className={TW_AppSidebarMenuIcon + ' mr-1 flex-shrink-0'} onClick={toggle} />
                    : <ArrowRight2 className={TW_AppSidebarMenuIcon + ' mr-1 flex-shrink-0'} onClick={toggle} />
                }
                <Folder2 className={TW_AppSidebarMenuIcon + ' mr-1 flex-shrink-0'} />
                <p className={TW_AppSidebarMenuText}>{folder.name}</p>
            </div>
            {isOpen && children.map(child => (
                <FolderNode
                    key={child.path}
                    folder={child}
                    depth={depth + 1}
                    selectedPath={selectedPath}
                    onSelect={onSelect}
                    fetchItems={fetchItems}
                />
            ))}
        </div>
    );
}

interface DriveSidebarProps {
    currentPath: string;
    onNavigate: (path: string) => void;
    fetchItems: FetchItemsFn;
    hasMembersRight: boolean;
}

function DriveSidebar({ currentPath, onNavigate, fetchItems, hasMembersRight }: DriveSidebarProps) {
    const [rootFolders, setRootFolders] = useState<DriveItem[]>([]);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const workspace = useModelStore(state => state.selectedWorkspace);
    const wsColor = useModelStore(state => state.wsColor);
    const wsHoverColor = useModelStore(state => state.wsHoverColor);
    const membersHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor, wsColor));

    useEffect(() => {
        fetchItems('').then(items => {
            setRootFolders(items.filter(i => i.type === FOLDER));
        });
    }, [fetchItems]);

    return (
        <div className={TW_DriveSidebar_Container}>
            <div className={TW_DriveSidebar_TitleBar}>
                <p className={TW_DriveSidebar_Title}>{t('app.drive.sidebar.title')}</p>
            </div>
            <div className={TW_DriveSidebar_FolderList}>
                {rootFolders.map(folder => (
                    <FolderNode
                        key={folder.path}
                        folder={folder}
                        depth={0}
                        selectedPath={currentPath}
                        onSelect={onNavigate}
                        fetchItems={fetchItems}
                    />
                ))}
            </div>
            {hasMembersRight && (
                <div className={TW_DriveSidebar_MembersFooter}>
                    <div onClick={() => navigate(`/workspaces/${workspace?._key}/members`)}
                         className={TW_AppSidebarMenuContainer} {...membersHover}>
                        <People className={TW_AppSidebarMenuIcon} />
                        <p className={TW_AppSidebarMenuText}>{t("app.sidebar.button.members")}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DriveSidebar;
